import React, { useRef } from 'react';
import clsx from 'clsx';
import { RequiredMark } from 'components/ui/FieldShell';
import { useFormLocked } from 'components/ui/FormLock';
import { Icon } from 'components/ui/Icon';
import styles from './SclaStep.module.scss';

interface IChoiceOption {
  label: string;
  value: string;
  icon?: string;
}

interface IChoiceChipsProps {
  label: string;
  id?: string;
  options: IChoiceOption[];
  value: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  variant?: 'chips' | 'cards';
  onChange: (value: string) => void;
}

export default function ChoiceChips({
  label,
  id,
  options,
  value,
  disabled = false,
  required = false,
  error,
  variant = 'chips',
  onChange,
}: IChoiceChipsProps) {
  const locked = useFormLocked();
  const isDisabled = disabled || locked;
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const groupId = label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const errorId = error ? `${groupId}-error` : undefined;

  const move = (index: number, direction: number) => {
    const next = (index + direction + options.length) % options.length;
    onChange(options[next].value);
    buttons.current[next]?.focus();
  };

  return (
    <div id={id}>
      <p className={styles.question} id={`${groupId}-label`}>
        {label}
        {required ? <RequiredMark /> : null}
      </p>
      <div
        className={variant === 'cards' ? styles.options : styles.chips}
        role="radiogroup"
        aria-labelledby={`${groupId}-label`}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
      >
        {options.map((option, index) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              ref={element => {
                buttons.current[index] = element;
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              className={clsx(
                variant === 'cards' ? styles.option : styles.chip,
                selected && (variant === 'cards' ? styles.optionOn : styles.chipOn),
              )}
              disabled={isDisabled}
              onClick={() => {
                if (isDisabled) return;
                onChange(option.value);
              }}
              onKeyDown={event => {
                if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                  event.preventDefault();
                  move(index, 1);
                }
                if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                  event.preventDefault();
                  move(index, -1);
                }
              }}
            >
              {option.icon ? <span aria-hidden="true">{option.icon}</span> : null}
              {option.label}
            </button>
          );
        })}
      </div>
      {error ? (
        <p className={styles.fieldError} id={errorId} role="alert">
          <Icon name="alert-circle" size={16} />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}
